import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  FaShieldAlt,
} from "react-icons/fa";


import Footer from "./Footer";
import Navbar from "./Navbar";
import ApplyNow from "./ApplyNow";
import WhyChooseUs from "./WhyChooseUs";
import FinancialProducts from "./FinancialProducts";
import HowItWorks from "./HowItWorks";
import Testimonials from "./Testimonials";
import LoanCalculator from "./LoanCalculator";
import StatusModal from "./StatusModal";
import Seo from "./Seo";


function CompanyLanding() {
  const heroRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Handle Hash Scroll
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  // Form State for Hero Form
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Submission from Home",
          sender_name: formData.fullName,
          sender_email: formData.email,
          phone: formData.phone,
          message: formData.message,
          consent: formData.consent ? "Accepted" : "Not Accepted",
        }),
      });
      const data = await response.json();
      if (data.success) {
        setModalConfig({
          isOpen: true,
          type: 'success',
          title: 'Application Sent!',
          message: "We've received your request. Our team will contact you within 2-4 business hours.",
        });
        setFormData({ fullName: "", email: "", phone: "", consent: true, message: "" });
      } else {
        setModalConfig({
          isOpen: true,
          type: 'error',
          title: 'Submission Failed',
          message: 'Something went wrong. Please try again later.',
        });
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Error Occurred',
        message: 'A network error occurred. Please check your connection and try again.',
      });
    } finally {
      setLoading(false);
    }
  };



  const websiteLink = `${import.meta.env.VITE_WEBSITE_LINK}/?utm_source=Home-Page&utm_medium=SEO`;
  const inputClass = "w-full px-4 py-3 rounded-lg bg-white text-[var(--text-primary)] font-medium placeholder:text-gray-300 transition-all duration-300 text-sm shadow-sm";

  return (
    <div className="min-h-screen w-full bg-white  relative overflow-x-hidden font-sans  selection:bg-[var(--primary)] selection:text-white">
      {/* Background Subtle Gradient */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-full h-full " />
      </div>

      <div className="relative z-10 flex w-full flex-col items-center text-[var(--text-primary)]">
        <Seo
          title="Instant Personal Loan in 10 Minutes | ToofanLoan"
          description="Secure 100% digital KYC loans from an RBI-registered NBFC. Get fast 10 minutes evaluation, clear Key Fact Statements, and safe direct bank transfer."
          path="/"
        />
        <Navbar />

        {/* Hero Section */}
        <section ref={heroRef} className="w-full max-w-[1440px] px-6 lg:px-12 pt-36 md:pt-40 ">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center mb-12 ">

            {/* Left Content */}
            <div className="col-span-1 lg:col-span-12 xl:col-span-7 hero-content space-y-6 md:space-y-8 animate-in fade-in slide-in-from-left duration-1000">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--success-bg)] border border-[var(--success-text)]/20 rounded-full">
                <FaShieldAlt className="text-[var(--success-text)] text-xs" />
                <span className="text-[var(--success-text)]  text-[10px] uppercase tracking-widest">System: Secure & Encrypted</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-black leading-tight text-black tracking-tight">
                Safe Loans.<br />
                Direct Transfer.<br />
                <span className="text-[var(--primary)]">10 Minutes.</span>
              </h1>

              <p className="text-base md:text-xl text-[var(--text-gray)] font-medium leading-relaxed max-w-xl opacity-80">
                Bridging the gap between your needs and your next paycheck. High-speed credit lines designed for the modern Indian workforce, backed by RBI regulated excellence.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 md:pt-6 w-full">
                <a
                  href={websiteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-10 md:px-12 py-4 md:py-5 bg-[var(--primary)] text-white font-black text-xs md:text-sm uppercase tracking-widest rounded-xl shadow-lg shadow-[var(--primary)]/20 hover:scale-105 active:scale-95 transition-all text-center"
                >
                  Start Application
                </a>
                <a
                  href={websiteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-10 md:px-12 py-4 md:py-5 bg-white text-[var(--text-gray)] font-black text-xs md:text-sm uppercase tracking-widest rounded-xl border border-[var(--border-light)] hover:bg-gray-50 transition-all shadow-sm">
                  Check Eligibility
                </a>
              </div>
            </div>

            {/* Right Form Card */}
            <div className="col-span-1 lg:col-span-12 xl:col-span-5 hero-visual animate-in fade-in slide-in-from-right duration-1000 mt-8 lg:mt-0">
              <div className="bg-[#F7FAFC] rounded-[32px] p-6 md:p-8 lg:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100 relative overflow-hidden">
                <form onSubmit={handleSubmit} className="relative z-10 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">Name</label>
                      <input type="text" name="fullName" placeholder="Enter your Name" value={formData.fullName} onChange={handleChange} required className={inputClass} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">Email</label>
                      <input type="email" name="email" placeholder="Enter your Email" value={formData.email} onChange={handleChange} required className={inputClass} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">Phone Number</label>
                    <input type="tel" name="phone" placeholder="Enter your Phone Number" value={formData.phone} onChange={handleChange} required className={inputClass} />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">Your Message</label>
                    <textarea name="message" placeholder="How can we help?" value={formData.message} onChange={handleChange} rows={3} className={inputClass} />
                  </div>

                  <div className="flex items-start gap-3 pt-1">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={formData.consent}
                      onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-[var(--primary)] shrink-0 cursor-pointer"
                    />
                    <label htmlFor="consent" className="text-[10px] font-bold text-gray-400 leading-snug cursor-pointer">
                      By continuing, you accept our <a href="#" className="text-[var(--primary)] hover:underline">T&Cs</a> & <a href="#" className="text-[var(--primary)] hover:underline">Privacy Policy</a>.
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 md:py-5 bg-[var(--primary)] text-white font-black uppercase tracking-[0.15em] text-xs md:text-sm rounded-xl shadow-xl shadow-[var(--primary)]/20 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {loading ? "Processing..." : "Contact Us"}
                  </button>

                  <div className="text-center pt-2">
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest opacity-80">
                      RBI Registered NBFC Partner | Secure 256-bit Encryption
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>

        </section>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mt-12 py-10 md:py-12 border-t border-[var(--border-light)]/50 bg-[var(--background-gradient-middle)]">
        {[
          {
            icon: <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 40C11.3667 38.8333 7.54167 36.175 4.525 32.025C1.50833 27.875 0 23.2667 0 18.2V6L16 0L32 6V18.2C32 23.2667 30.4917 27.875 27.475 32.025C24.4583 36.175 20.6333 38.8333 16 40ZM16 35.8C19.2333 34.8 21.9333 32.825 24.1 29.875C26.2667 26.925 27.5333 23.6333 27.9 20H16V4.25L4 8.75V18.2C4 18.5667 4 18.8667 4 19.1C4 19.3333 4.03333 19.6333 4.1 20H16V35.8Z" fill="#004AC6" />
            </svg>
            , label: "PCI-DSS Compliant"
          },
          {
            icon: <svg width="37" height="40" viewBox="0 0 37 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.99 4.9C21.5233 4.9 24.8567 5.65833 27.99 7.175C31.1233 8.69167 33.74 10.8833 35.84 13.75C36.0733 14.05 36.1483 14.3167 36.065 14.55C35.9817 14.7833 35.84 14.9833 35.64 15.15C35.44 15.3167 35.2067 15.3917 34.94 15.375C34.6733 15.3583 34.44 15.2167 34.24 14.95C32.4067 12.35 30.0483 10.3583 27.165 8.975C24.2817 7.59167 21.2233 6.9 17.99 6.9C14.7567 6.9 11.7233 7.59167 8.89 8.975C6.05667 10.3583 3.70667 12.35 1.84 14.95C1.64 15.25 1.40667 15.4167 1.14 15.45C0.873333 15.4833 0.64 15.4167 0.44 15.25C0.206667 15.0833 0.065 14.875 0.015 14.625C-0.035 14.375 0.04 14.1167 0.24 13.85C2.30667 11.0167 4.89833 8.81667 8.015 7.25C11.1317 5.68333 14.4567 4.9 17.99 4.9ZM17.99 9.6C22.49 9.6 26.3567 11.1 29.59 14.1C32.8233 17.1 34.44 20.8167 34.44 25.25C34.44 26.9167 33.8483 28.3083 32.665 29.425C31.4817 30.5417 30.04 31.1 28.34 31.1C26.64 31.1 25.1817 30.5417 23.965 29.425C22.7483 28.3083 22.14 26.9167 22.14 25.25C22.14 24.15 21.7317 23.225 20.915 22.475C20.0983 21.725 19.1233 21.35 17.99 21.35C16.8567 21.35 15.8817 21.725 15.065 22.475C14.2483 23.225 13.84 24.15 13.84 25.25C13.84 28.4833 14.7983 31.1833 16.715 33.35C18.6317 35.5167 21.1067 37.0333 24.14 37.9C24.44 38 24.64 38.1667 24.74 38.4C24.84 38.6333 24.8567 38.8833 24.79 39.15C24.7233 39.3833 24.59 39.5833 24.39 39.75C24.19 39.9167 23.94 39.9667 23.64 39.9C20.1733 39.0333 17.34 37.3083 15.14 34.725C12.94 32.1417 11.84 28.9833 11.84 25.25C11.84 23.5833 12.44 22.1833 13.64 21.05C14.84 19.9167 16.29 19.35 17.99 19.35C19.69 19.35 21.14 19.9167 22.34 21.05C23.54 22.1833 24.14 23.5833 24.14 25.25C24.14 26.35 24.5567 27.275 25.39 28.025C26.2233 28.775 27.2067 29.15 28.34 29.15C29.4733 29.15 30.44 28.775 31.24 28.025C32.04 27.275 32.44 26.35 32.44 25.25C32.44 21.3833 31.0233 18.1333 28.19 15.5C25.3567 12.8667 21.9733 11.55 18.04 11.55C14.1067 11.55 10.7233 12.8667 7.89 15.5C5.05667 18.1333 3.64 21.3667 3.64 25.2C3.64 26 3.715 27 3.865 28.2C4.015 29.4 4.37333 30.8 4.94 32.4C5.04 32.7 5.03167 32.9667 4.915 33.2C4.79833 33.4333 4.60667 33.6 4.34 33.7C4.07333 33.8 3.815 33.7917 3.565 33.675C3.315 33.5583 3.14 33.3667 3.04 33.1C2.54 31.8 2.18167 30.5083 1.965 29.225C1.74833 27.9417 1.64 26.6167 1.64 25.25C1.64 20.8167 3.24833 17.1 6.465 14.1C9.68167 11.1 13.5233 9.6 17.99 9.6ZM17.99 0C20.1233 0 22.2067 0.258333 24.24 0.775C26.2733 1.29167 28.24 2.03333 30.14 3C30.44 3.16667 30.615 3.36667 30.665 3.6C30.715 3.83333 30.69 4.06667 30.59 4.3C30.49 4.53333 30.3233 4.71667 30.09 4.85C29.8567 4.98333 29.5733 4.96667 29.24 4.8C27.4733 3.9 25.6483 3.20833 23.765 2.725C21.8817 2.24167 19.9567 2 17.99 2C16.0567 2 14.1567 2.225 12.29 2.675C10.4233 3.125 8.64 3.83333 6.94 4.8C6.67333 4.96667 6.40667 5.00833 6.14 4.925C5.87333 4.84167 5.67333 4.66667 5.54 4.4C5.40667 4.13333 5.37333 3.89167 5.44 3.675C5.50667 3.45833 5.67333 3.26667 5.94 3.1C7.80667 2.1 9.75667 1.33333 11.79 0.8C13.8233 0.266667 15.89 0 17.99 0ZM17.99 14.45C21.09 14.45 23.7567 15.4917 25.99 17.575C28.2233 19.6583 29.34 22.2167 29.34 25.25C29.34 25.55 29.2483 25.7917 29.065 25.975C28.8817 26.1583 28.64 26.25 28.34 26.25C28.0733 26.25 27.84 26.1583 27.64 25.975C27.44 25.7917 27.34 25.55 27.34 25.25C27.34 22.75 26.415 20.6583 24.565 18.975C22.715 17.2917 20.5233 16.45 17.99 16.45C15.4567 16.45 13.2817 17.2917 11.465 18.975C9.64833 20.6583 8.74 22.75 8.74 25.25C8.74 27.95 9.20667 30.2417 10.14 32.125C11.0733 34.0083 12.44 35.9 14.24 37.8C14.44 38 14.54 38.2333 14.54 38.5C14.54 38.7667 14.44 39 14.24 39.2C14.04 39.4 13.8067 39.5 13.54 39.5C13.2733 39.5 13.04 39.4 12.84 39.2C10.8733 37.1333 9.365 35.025 8.315 32.875C7.265 30.725 6.74 28.1833 6.74 25.25C6.74 22.2167 7.84 19.6583 10.04 17.575C12.24 15.4917 14.89 14.45 17.99 14.45ZM17.94 24.25C18.24 24.25 18.4817 24.35 18.665 24.55C18.8483 24.75 18.94 24.9833 18.94 25.25C18.94 27.75 19.84 29.8 21.64 31.4C23.44 33 25.54 33.8 27.94 33.8C28.14 33.8 28.4233 33.7833 28.79 33.75C29.1567 33.7167 29.54 33.6667 29.94 33.6C30.24 33.5333 30.4983 33.575 30.715 33.725C30.9317 33.875 31.0733 34.1 31.14 34.4C31.2067 34.6667 31.1567 34.9 30.99 35.1C30.8233 35.3 30.6067 35.4333 30.34 35.5C29.74 35.6667 29.215 35.7583 28.765 35.775C28.315 35.7917 28.04 35.8 27.94 35.8C24.9733 35.8 22.3983 34.8 20.215 32.8C18.0317 30.8 16.94 28.2833 16.94 25.25C16.94 24.9833 17.0317 24.75 17.215 24.55C17.3983 24.35 17.64 24.25 17.94 24.25Z" fill="#004AC6" />
            </svg>
            , label: "100% Digital KYC"
          },
          {
            icon: <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M26.4 17.9L17.1 8.6C17.5667 8.4 18.0417 8.25 18.525 8.15C19.0083 8.05 19.5 8 20 8C21.9667 8 23.625 8.675 24.975 10.025C26.325 11.375 27 13.0333 27 15C27 15.5 26.95 15.9917 26.85 16.475C26.75 16.9583 26.6 17.4333 26.4 17.9ZM7.7 30.2C9.4 28.9 11.3 27.875 13.4 27.125C15.5 26.375 17.7 26 20 26C20.6 26 21.175 26.025 21.725 26.075C22.275 26.125 22.85 26.2 23.45 26.3L19.05 21.9C17.4833 21.7 16.1417 21.0417 15.025 19.925C13.9083 18.8083 13.25 17.4667 13.05 15.9L7.35 10.2C6.28333 11.5667 5.45833 13.075 4.875 14.725C4.29167 16.375 4 18.1333 4 20C4 21.9667 4.325 23.8167 4.975 25.55C5.625 27.2833 6.53333 28.8333 7.7 30.2ZM32.6 29.8C33.6667 28.4333 34.5 26.925 35.1 25.275C35.7 23.625 36 21.8667 36 20C36 15.5667 34.4417 11.7917 31.325 8.675C28.2083 5.55833 24.4333 4 20 4C18.1333 4 16.375 4.3 14.725 4.9C13.075 5.5 11.5667 6.33333 10.2 7.4L32.6 29.8ZM20.025 40C17.275 40 14.6833 39.475 12.25 38.425C9.81667 37.375 7.69167 35.9417 5.875 34.125C4.05833 32.3083 2.625 30.1833 1.575 27.75C0.525 25.3167 0 22.725 0 19.975C0 17.225 0.525 14.6417 1.575 12.225C2.625 9.80833 4.05833 7.69167 5.875 5.875C7.69167 4.05833 9.81667 2.625 12.25 1.575C14.6833 0.525 17.275 0 20.025 0C22.775 0 25.3583 0.525 27.775 1.575C30.1917 2.625 32.3083 4.05833 34.125 5.875C35.9417 7.69167 37.375 9.80833 38.425 12.225C39.475 14.6417 40 17.225 40 19.975C40 22.725 39.475 25.3167 38.425 27.75C37.375 30.1833 35.9417 32.3083 34.125 34.125C32.3083 35.9417 30.1917 37.375 27.775 38.425C25.3583 39.475 22.775 40 20.025 40ZM20 36C21.7667 36 23.4333 35.7417 25 35.225C26.5667 34.7083 28 33.9667 29.3 33C28 32.0333 26.5667 31.2917 25 30.775C23.4333 30.2583 21.7667 30 20 30C18.2333 30 16.5667 30.2583 15 30.775C13.4333 31.2917 12 32.0333 10.7 33C12 33.9667 13.4333 34.7083 15 35.225C16.5667 35.7417 18.2333 36 20 36Z" fill="#004AC6" />
            </svg>
            , label: "No Upfront Fees"
          },
          {
            icon: <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.9 27.1L25.2 15.8L22.35 12.95L13.9 21.4L9.7 17.2L6.85 20.05L13.9 27.1ZM16 40C11.3667 38.8333 7.54167 36.175 4.525 32.025C1.50833 27.875 0 23.2667 0 18.2V6L16 0L32 6V18.2C32 23.2667 30.4917 27.875 27.475 32.025C24.4583 36.175 20.6333 38.8333 16 40ZM16 35.8C19.4667 34.7 22.3333 32.5 24.6 29.2C26.8667 25.9 28 22.2333 28 18.2V8.75L16 4.25L4 8.75V18.2C4 22.2333 5.13333 25.9 7.4 29.2C9.66667 32.5 12.5333 34.7 16 35.8Z" fill="#004AC6" />
            </svg>
            , label: "ISO 27001 Certified"
          }
        ].map((badge, i) => (
          <div key={i} className="trust-badge flex flex-col items-center gap-4 text-center group cursor-default">
            <div className="text-[var(--primary)] text-3xl md:text-4xl opacity-80 group-hover:scale-110 group-hover:opacity-100 transition-all duration-300">
              {badge.icon}
            </div>
            <span className="text-[10px] md:text-[11px] font-black text-[var(--text-gray)] uppercase tracking-[0.2em] opacity-80 group-hover:opacity-100 transition-opacity">
              {badge.label}
            </span>
          </div>
        ))}
      </div>
      <WhyChooseUs />
      <LoanCalculator />
      <FinancialProducts />
      <div id="how-it-works">
        <HowItWorks />
      </div>
      <Testimonials />
      <ApplyNow />

      <Footer />

      <StatusModal
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
}

export default CompanyLanding;
